import { ReactNode, useCallback, useEffect, useRef } from 'react'
import Box from '@mui/material/Box'
import { useChat } from '@ai-sdk/react'
import type { Chat } from '@ai-sdk/react'
import AssistantAnnouncer from './AssistantAnnouncer'
import AssistantBriefingCard from './AssistantBriefingCard'
import AssistantComposer from './AssistantComposer'
import AssistantEmptyState from './AssistantEmptyState'
import AssistantMessages from './AssistantMessages'
import AssistantNudgeCard from './AssistantNudgeCard'
import {
  useAssistant,
  type AssistantMessage,
  type AssistantPendingQuestion,
} from './AssistantProvider'
import type { AssistantPageSuggestions } from './pageSuggestions'
import {
  type AssistantQuestionSource,
  type AssistantSurface,
  trackAssistantEvent,
} from './analytics'
import type { AssistantApprovalDecision } from './approvals/AssistantApprovalPart'

interface AssistantChatProps {
  chat: Chat<AssistantMessage>
  /** Title bar for the surface (docked panel or page). */
  renderHeader?: (hasMessages: boolean) => ReactNode
  autoFocus?: boolean
  /** Which surface this is, for usage counts. */
  surface: AssistantSurface
  /** Starter questions for the page the user is on. */
  suggestions?: AssistantPageSuggestions
}

/**
 * The conversation itself: messages, the friendly empty state and the
 * composer. Shared by the docked panel and the /assistant page. The
 * nudge's briefing card, when open, takes the empty state's place, or sits
 * just above the composer after an existing thread. In the panel, the empty
 * state also offers the nudge ("Needs you"), and a question asked in the top
 * bar is sent as soon as the chat is free.
 */
const AssistantChat = ({
  chat,
  renderHeader,
  autoFocus,
  surface,
  suggestions,
}: AssistantChatProps) => {
  const inputRef = useRef<HTMLTextAreaElement>(null)
  const {
    messages,
    status,
    error,
    sendMessage,
    regenerate,
    stop,
    clearError,
    addToolApprovalResponse,
  } = useChat({ chat })
  const isBusy = status === 'submitted' || status === 'streaming'
  // Outside the provider (tests, the gallery) there is no briefing.
  const assistant = useAssistant()
  const briefing = assistant?.briefing ?? null
  const clearBriefing = assistant?.clearBriefing

  const send = useCallback(
    (text: string, source: AssistantQuestionSource, focusCaseId?: string) => {
      if (error) clearError()
      // Any message moves the conversation on; the card has done its job.
      clearBriefing?.()

      trackAssistantEvent({
        event: 'assistant__question_asked',
        properties: { surface, source },
      })
      // A task question names its case by title; the id goes along as the
      // message's metadata, which the transport sends as context.
      sendMessage(focusCaseId ? { text, metadata: { focusCaseId } } : { text })
    },
    [error, clearError, clearBriefing, surface, sendMessage]
  )

  // Asked in the top bar before the panel opened. Sent once the chat is
  // free: an answer from before the panel closed may still be coming in.
  const pendingQuestion =
    surface === 'panel' ? assistant?.pendingQuestion ?? null : null
  const clearPendingQuestion = assistant?.clearPendingQuestion
  const sentQuestion = useRef<AssistantPendingQuestion | null>(null)
  useEffect(() => {
    if (!pendingQuestion || isBusy || sentQuestion.current === pendingQuestion)
      return
    sentQuestion.current = pendingQuestion
    clearPendingQuestion?.()
    send(pendingQuestion.text, pendingQuestion.source, pendingQuestion.caseId)
  }, [pendingQuestion, isBusy, clearPendingQuestion, send])

  // The panel's chat stays mounted while it closes: opened again before it
  // has gone, the composer's autoFocus doesn't run, so focus it here.
  const isPanelOpen = surface === 'panel' && Boolean(assistant?.isPanelOpen)
  useEffect(() => {
    if (autoFocus && isPanelOpen) inputRef.current?.focus()
  }, [autoFocus, isPanelOpen])

  // Narrow screens open the panel from the icon, so the nudge is offered here.
  const nudge = surface === 'panel' ? assistant?.nudge ?? null : null

  // Asks the last question again; drops a half-finished answer first.
  const retry = () => {
    clearError()
    regenerate()
  }

  // The provider's Chat sends the answer once every card in the turn has one.
  const decide = ({ id, approved, action }: AssistantApprovalDecision) => {
    if (error) clearError()
    trackAssistantEvent({
      event: 'assistant__change_decided',
      properties: {
        surface,
        action,
        decision: approved ? 'approved' : 'cancelled',
      },
    })
    // The card's buttons are about to disappear; don't strand keyboard focus.
    inputRef.current?.focus()
    addToolApprovalResponse({
      id,
      approved,
      reason: approved ? undefined : 'The user cancelled.',
    })
  }

  const briefingCard = briefing && (
    <AssistantBriefingCard
      briefing={briefing}
      disabled={isBusy}
      onFollowUp={(text, caseId) => {
        // The card's chips are about to disappear; keep focus in the chat.
        inputRef.current?.focus()
        send(text, 'briefing', caseId)
      }}
    />
  )

  let conversation: ReactNode
  if (messages.length > 0) {
    conversation = (
      <AssistantMessages
        messages={messages}
        status={status}
        error={error}
        onRetry={retry}
        onReply={text => send(text, 'quick_reply')}
        onApproval={decide}
      />
    )
  } else if (briefing) {
    conversation = (
      <Box sx={{ flex: 1, minHeight: 0, overflowY: 'auto', p: 2 }}>
        {briefingCard}
      </Box>
    )
  } else {
    conversation = (
      <AssistantEmptyState
        onPick={(text, caseId) => send(text, 'suggestion', caseId)}
        disabled={isBusy}
        suggestions={suggestions}
        needsYou={
          nudge &&
          assistant && (
            <AssistantNudgeCard
              variant="inline"
              nudge={nudge}
              // The card is about to go; keep focus in the chat.
              onReview={() => {
                inputRef.current?.focus()
                assistant.reviewNudge(nudge)
              }}
              onDismiss={() => {
                inputRef.current?.focus()
                assistant.dismissNudge(nudge)
              }}
            />
          )
        }
      />
    )
  }

  return (
    <Box
      data-testid="assistant-chat"
      sx={{
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        minHeight: 0,
        bgcolor: 'background.paper',
      }}
    >
      {renderHeader?.(messages.length > 0 || Boolean(briefing))}
      {conversation}
      {messages.length > 0 && briefing && (
        // Capped, so on a short panel the thread and composer keep room.
        <Box
          sx={{
            px: 2,
            pt: 1,
            pb: 0.5,
            flexShrink: 0,
            maxHeight: '45%',
            overflowY: 'auto',
          }}
        >
          {briefingCard}
        </Box>
      )}
      <AssistantAnnouncer
        messages={messages}
        status={status}
        conversation={chat}
      />
      <AssistantComposer
        onSend={text => send(text, 'typed')}
        inputRef={inputRef}
        onStop={stop}
        isBusy={isBusy}
        autoFocus={autoFocus}
      />
    </Box>
  )
}

export default AssistantChat
